import { useIntl } from 'react-intl';

import Grafico from 'src/componentes/chart';
import FavorabilidadeCard from './FavorabilidadeCard';
import { findLikertRange, LIKERT_COLORS, toNumber } from './likertHelpers';
import LikertPorPerguntaTabela from './LikertPorPerguntaTabela';

const styles = {
  heading: {
    marginBottom: 18,
  },
  title: {
    color: '#202124',
    fontSize: 20,
    fontWeight: 700,
    marginBottom: 4,
  },
  subtitle: {
    color: '#7b7f87',
    fontSize: 14,
  },
  card: {
    alignItems: 'center',
    backgroundColor: '#fff',
    border: '1px solid #dfe3e8',
    borderRadius: 9,
    display: 'flex',
    flexWrap: 'wrap',
    gap: 28,
    padding: '24px 22px',
  },
  chartColumn: {
    flex: '0 1 250px',
    margin: '0 auto',
    minWidth: 230,
  },
  chartWrapper: {
    height: 230,
    position: 'relative',
  },
  chartCenter: {
    alignItems: 'center',
    bottom: 0,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    left: 0,
    pointerEvents: 'none',
    position: 'absolute',
    right: 0,
    top: 0,
  },
  average: {
    color: '#17191c',
    fontSize: 31,
    fontWeight: 700,
    lineHeight: 1.1,
  },
  averageLabel: {
    color: '#7b7f87',
    fontSize: 13,
    marginTop: 5,
  },
  chartLegend: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 17,
    justifyContent: 'center',
    marginTop: 2,
  },
  legendItem: {
    alignItems: 'center',
    color: '#7b7f87',
    display: 'flex',
    fontSize: 13,
    gap: 6,
  },
  dot: {
    borderRadius: '50%',
    display: 'inline-block',
    flexShrink: 0,
    height: 10,
    width: 10,
  },
  tableWrapper: {
    flex: '1 1 430px',
    minWidth: 0,
  },
  table: {
    borderCollapse: 'collapse',
    width: '100%',
  },
  th: {
    borderBottom: '1px solid #e5e7eb',
    color: '#7b7f87',
    fontSize: 13,
    fontWeight: 400,
    padding: '8px 4px 11px',
    textAlign: 'left',
  },
  thRight: {
    textAlign: 'right',
  },
  td: {
    borderBottom: '1px solid #e5e7eb',
    color: '#202124',
    fontSize: 14,
    padding: '10px 4px',
  },
  tdRight: {
    textAlign: 'right',
  },
  category: {
    alignItems: 'center',
    display: 'flex',
    gap: 8,
  },
  interval: {
    color: '#7b7f87',
    fontSize: 12,
    marginLeft: 18,
    marginTop: 2,
  },
  totalLabel: {
    fontWeight: 700,
  },
  totalValue: {
    fontWeight: 700,
    textAlign: 'right',
  },
};

const formatLimit = (intl, value) =>
  `${intl.formatNumber(toNumber(value), { maximumFractionDigits: 1 })}%`;

const formatInterval = (intl, range, fallback) => {
  const lower = range.limiteInferior;
  const upper = range.limiteSuperior;

  if (lower == null || upper == null) return fallback;

  const lowerText = formatLimit(intl, lower);
  const upperText = formatLimit(intl, upper);
  const includeLower = range.incluirLimiteInferior !== false;
  const includeUpper = range.incluirLimiteSuperior !== false;

  if (includeLower && includeUpper) {
    return `${lowerText} – ${upperText}`;
  }

  return `${includeLower ? '≥' : '>'} ${lowerText} e ${
    includeUpper ? '≤' : '<'
  } ${upperText}`;
};

function LikertFavorabilidade({ dados }) {
  const intl = useIntl();
  const geral = dados?.geral || dados || {};
  const ranges = geral.faixas;

  const categories = [
    {
      code: 'positiva',
      label: intl.formatMessage({
        id: 'labelLikertPositiva',
      }),
      color: LIKERT_COLORS.positiva,
      fallbackInterval: '70% – 100%',
    },
    {
      code: 'neutra',
      label: intl.formatMessage({
        id: 'labelLikertNeutra',
      }),
      color: LIKERT_COLORS.neutra,
      fallbackInterval: '> 40% e < 70%',
    },
    {
      code: 'negativa',
      label: intl.formatMessage({
        id: 'labelLikertNegativa',
      }),
      color: LIKERT_COLORS.negativa,
      fallbackInterval: '0% – 40%',
    },
  ].map((category) => {
    const range = findLikertRange(ranges, category.code);

    return {
      ...category,
      quantidade: toNumber(range.quantidade),
      percentual: toNumber(range.percentual),
      interval: formatInterval(intl, range, category.fallbackInterval),
    };
  });

  const totalRespostas = toNumber(geral.totalRespostas);
  const rawAverage =
    geral.mediaGeralPercentual == null
      ? geral.mediaGeral
      : geral.mediaGeralPercentual;
  const averageValue = rawAverage == null ? null : toNumber(rawAverage);
  const averageLabel =
    averageValue == null
      ? '–'
      : `${intl.formatNumber(averageValue, {
          minimumFractionDigits: 1,
          maximumFractionDigits: 1,
        })}%`;

  const chartConfig = {
    type: 'doughnut2d',
    width: '100%',
    height: 230,
    dataFormat: 'json',
    dataSource: {
      chart: {
        theme: 'fusion',
        animation: '0',
        bgAlpha: '0',
        canvasBgAlpha: '0',
        doughnutRadius: '70',
        enableMultiSlicing: '0',
        pieRadius: '100',
        showBorder: '0',
        showLabels: '0',
        showLegend: '0',
        showPercentValues: '0',
        showShadow: '0',
        showValues: '0',
        startingAngle: '90',
        toolTipBgColor: '#ffffff',
        toolTipBorderColor: '#dfe3e8',
        use3DLighting: '0',
      },
      data: categories.map((category) => ({
        label: category.label,
        value: category.quantidade,
        color: category.color,
        toolText: `${category.label}: <b>${intl.formatNumber(
          category.percentual,
          { maximumFractionDigits: 1 }
        )}%</b>`,
      })),
    },
  };

  const formatPercent = (value) =>
    `${intl.formatNumber(toNumber(value), {
      maximumFractionDigits: 1,
    })}%`;

  const perguntas = dados?.perguntas || geral.perguntas;

  return (
    <div>
      <div style={styles.heading}>
        <div style={styles.title}>
          {intl.formatMessage({
            id: 'labelLikertFavorabilidade',
          })}
        </div>
        <div style={styles.subtitle}>
          {intl.formatMessage({
            id: 'labelLikertFavorabilidadeDescricao',
          })}
        </div>
      </div>

      <FavorabilidadeCard dados={dados?.favorabilidade} />

      <div style={styles.card}>
        <div style={styles.chartColumn}>
          <div style={styles.chartWrapper}>
            <Grafico {...chartConfig} />
            <div style={styles.chartCenter}>
              <span style={styles.average}>{averageLabel}</span>
              <span style={styles.averageLabel}>
                {intl.formatMessage({
                  id: 'labelLikertMediaGeral',
                })}
              </span>
            </div>
          </div>
          <div style={styles.chartLegend}>
            {categories.map((category) => (
              <div key={category.code} style={styles.legendItem}>
                <span
                  style={{ ...styles.dot, backgroundColor: category.color }}
                />
                <span>{category.label}</span>
              </div>
            ))}
          </div>
        </div>

        <div style={styles.tableWrapper}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>
                  {intl.formatMessage({
                    id: 'labelCategoria',
                  })}
                </th>
                <th style={{ ...styles.th, ...styles.thRight }}>
                  {intl.formatMessage({
                    id: 'labelQuantidade',
                  })}
                </th>
                <th style={{ ...styles.th, ...styles.thRight }}>
                  {intl.formatMessage({
                    id: 'labelPercentual',
                  })}
                </th>
              </tr>
            </thead>
            <tbody>
              {categories.map((category) => (
                <tr key={category.code}>
                  <td style={styles.td}>
                    <div style={styles.category}>
                      <span
                        style={{
                          ...styles.dot,
                          backgroundColor: category.color,
                        }}
                      />
                      <span>{category.label}</span>
                    </div>
                    <div style={styles.interval}>{category.interval}</div>
                  </td>
                  <td style={{ ...styles.td, ...styles.tdRight }}>
                    {category.quantidade}
                  </td>
                  <td style={{ ...styles.td, ...styles.tdRight }}>
                    {formatPercent(category.percentual)}
                  </td>
                </tr>
              ))}
              <tr>
                <td style={{ ...styles.td, ...styles.totalLabel }}>
                  {intl.formatMessage({
                    id: 'labelTotal',
                  })}
                </td>
                <td style={{ ...styles.td, ...styles.totalValue }}>
                  {totalRespostas}
                </td>
                <td style={{ ...styles.td, ...styles.totalValue }}>100%</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <LikertPorPerguntaTabela
        faixasFavorabilidade={dados?.favorabilidade?.faixas}
        perguntas={perguntas}
      />
    </div>
  );
}

export default LikertFavorabilidade;
